import { shadeColor } from "./helpers";

/**
 * gordon-0915-graph-block-pie-donut-white-wedge-blurry-labels-legend-contrast
 *
 * A chart datapoint (Graph block: Bar/Line/Area/Pie/Donut/Scatter/Radar) must
 * never render white/transparent when its segment-palette color reference is
 * missing or was wiped (root cause: usePaletteColor's getSuggestedColor could
 * leak an empty/blank hex straight through when a single palette index was
 * cleared, and the Graph block's own render fallback collapsed every
 * color-less datapoint onto one identical constant). This module is the
 * single source of truth for "is this a real, paintable color" and "what do
 * we paint instead" so every chart type shares the same never-white rule and
 * the same per-datapoint shade steps (so two fallback wedges stay visually
 * distinct instead of collapsing onto one flat swatch).
 */

const UNUSABLE = new Set(["", "white", "transparent", "none", "inherit", "currentcolor"]);
// Bare hex-for-white in any case/shorthand form.
const WHITE_HEX = new Set(["#fff", "#ffffff"]);

export const isUsableColor = (color) => {
  if (typeof color !== "string") return false;
  const v = color.trim().toLowerCase();
  if (!v.length) return false;
  if (UNUSABLE.has(v)) return false;
  if (WHITE_HEX.has(v)) return false;
  return true;
};

// Last-resort base when there is truly no segment/wheel color to derive
// from — a real brand color, never plain white.
export const CHART_FALLBACK_BASE = "#6D5EF6";

// Per-index shade steps applied on top of a base color so consecutive
// fallback datapoints (e.g. two wiped colors in the same chart) stay
// distinguishable instead of rendering as one identical flat swatch.
const SHADE_STEPS = [0, -18, 16, -32, 28];

/**
 * Resolve the color to paint for a chart datapoint. `preferred` is the
 * caller's already-resolved color (segment/wheel palette hex); when it is
 * missing or unusable, derive one from `base` (typically the segment's own
 * color, or a wheel palette color) shaded by `index` so it never defaults to
 * white/transparent and never collapses two missing colors onto one hex.
 */
export const deriveChartColor = (preferred, base, index = 0) => {
  if (isUsableColor(preferred)) return preferred;
  const root = isUsableColor(base) ? base : CHART_FALLBACK_BASE;
  const step = SHADE_STEPS[((index % SHADE_STEPS.length) + SHADE_STEPS.length) % SHADE_STEPS.length];
  if (step === 0) return root;
  const shaded = shadeColor(root, step);
  return isUsableColor(shaded) ? shaded : root;
};
